import { Component, ElementRef, Input, OnChanges } from "@angular/core";
import { Link } from "@common/ADAPT.Common.Model/organisation/link";
import { Objective } from "@common/ADAPT.Common.Model/organisation/objective";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { filter } from "rxjs";
import { IObjectiveTeamGroup, ObjectivesService } from "../objectives.service";
import { ObjectivesUiService } from "../objectives-ui.service";

@Component({
    selector: "adapt-display-linked-objectives",
    templateUrl: "./display-linked-objectives.component.html",
    styleUrl: "./display-linked-objectives.component.scss",
    standalone: false,
})
export class DisplayLinkedObjectivesComponent extends BaseComponent implements OnChanges {
    @Input() public links: Link[] = [];
    @Input() public objectives?: Objective[];
    @Input() public objective?: Objective;
    @Input() public hasEditPermissions = false;
    @Input() public showGroupObjectiveTextSuffix = true;
    @Input() public blurTitle = false;
    @Input() public currentPageTeamId?: number;

    public teamGroupedObjectives?: IObjectiveTeamGroup[];

    public constructor(
        private objectivesService: ObjectivesService,
        private objectivesUiService: ObjectivesUiService,
        elementRef: ElementRef,
        rxjsBreezeService: RxjsBreezeService,
    ) {
        super(elementRef);

        rxjsBreezeService.entityTypeChanged(Link).pipe(
            filter((link) => link.objective1Id === this.objective?.objectiveId || link.objective2Id === this.objective?.objectiveId),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.ngOnChanges());

        // change of objective status requires reordering
        rxjsBreezeService.entityTypeChanged(Objective).pipe(
            filter((objective) => !!this.objectives?.some((o) => o.objectiveId === objective.objectiveId)),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.ngOnChanges());
    }

    public ngOnChanges() {
        if (this.objective) {
            const linkedObjectives = this.objective.links
                .filter((l) => l.extensions.isObjectiveToObjectiveLink && l.objective2)
                .map((link) => link.objective2) as Objective[];
            this.teamGroupedObjectives = this.objectivesService.groupObjectivesByTeam(linkedObjectives);
            this.sortTeamGroupedObjectives();
        } else if (this.objectives) {
            this.teamGroupedObjectives = this.objectivesService.groupObjectivesByTeam(this.objectives);
            this.sortTeamGroupedObjectives();
        } else if (this.links) {
            const objectiveIds = this.links
                .filter((l) => !l.objective1 && !!l.objective1Id)
                .map((l) => l.objective1Id!)
                .sort();

            this.objectivesService.getObjectivesByIds(objectiveIds).subscribe(() => {
                this.teamGroupedObjectives = this.objectivesService.groupObjectivesByTeam(this.links.map((l) => l.objective1!));
                this.sortTeamGroupedObjectives();
            });
        } else {
            this.teamGroupedObjectives = [];
        }
    }

    public sortTeamGroupedObjectives() {
        // if not defined or organisation, no additional sort is required as org will also go first
        if (this.currentPageTeamId && this.teamGroupedObjectives) {
            // current team group should go first as there won't be a redirect link
            this.teamGroupedObjectives.sort((g1, g2) => {
                if (g1.team?.teamId === g2.team?.teamId) {
                    return 0; // same team, no change
                } else if (this.currentPageTeamId === g1.team?.teamId) {
                    return -1; // g1 before g2
                } else if (this.currentPageTeamId === g2.team?.teamId) {
                    return 1; // g2 before g1
                } else {
                    return 0; // both g1 g2 not current team -> unchanged
                }
            });
        }
    }

    public removeObjectiveLink(target: Objective) {
        if (this.objective) {
            const link = this.objective.links.find((l) => l.objective2 === target);
            if (link) {
                this.objectivesUiService.promptToDeleteObjectiveLink(link).pipe(
                    this.takeUntilDestroyed(),
                ).subscribe();
            }

        }
    }
}
